import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { ArrowLeft, Check, Eye, EyeOff } from 'lucide-react'
import { INDUSTRY_OPTIONS } from '../../utils/industrySchemas'
import { NSSelect } from '../shared/ns-kit'
import LoadingSpinner from '../common/LoadingSpinner'

function Field({ label, hint, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

function CardSection({ title, desc, right, children, danger }) {
  return (
    <div className="card card-pad" style={danger ? { border: '1px solid rgba(255,107,107,0.3)' } : {}}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1 }}>
          <h2 className="h2" style={danger ? { color: 'var(--danger)' } : {}}>{title}</h2>
          {desc && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>{desc}</div>}
        </div>
        {right}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  )
}

const INDUSTRIES = INDUSTRY_OPTIONS.map(o => o.label)

const NewClient = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { subscription_plan: 'basic', edit_quota: 50, industry: 'general' }
  })

  const { data: billingPlans, isLoading: plansLoading } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data)
  )

  const subscriptionPlan = watch('subscription_plan')

  const createClientMutation = useMutation(
    (clientData) => apiService.post('/clients', clientData),
    {
      onSuccess: () => {
        toast.success('Client created successfully!')
        queryClient.invalidateQueries(['clients'])
        navigate('/admin/clients')
      },
      onError: (error) => toast.error(
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.msg ||
        'Failed to create client'
      )
    }
  )

  const onSubmit = (data) => {
    createClientMutation.mutate({
      name: data.name,
      email: data.email,
      password: data.password,
      company_name: data.company_name,
      subscription_plan: data.subscription_plan,
      edit_quota: parseInt(data.edit_quota),
      industry: data.industry || 'general',
      phone: data.phone || '',
      domain: data.domain ? `https://${data.domain.replace(/^https?:\/\//, '')}` : '',
      notes: data.notes || '',
    })
  }

  const planOptions = billingPlans
    ? Object.entries(billingPlans).map(([id, plan]) => ({ id, name: plan.name, price: plan.price, quota: plan.features.editsPerMonth }))
    : []

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
    return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  }

  const actions = (
    <>
      <button type="button" className="btn" onClick={() => navigate('/admin/clients')}>Cancel</button>
      <button type="button" className="btn btn-primary" onClick={handleSubmit(onSubmit)} disabled={createClientMutation.isLoading}>
        {createClientMutation.isLoading ? <LoadingSpinner size="sm" /> : <><Check style={{ width: 14, height: 14 }} /> Add client</>}
      </button>
    </>
  )

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start', marginBottom: 4 }} onClick={() => navigate('/admin/clients')}>
            <ArrowLeft style={{ width: 14, height: 14 }} /> Back
          </button>
          <div className="eyebrow">Clients · New</div>
          <h1>Add client</h1>
          <div className="subtitle">Create a new client account and assign their plan.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>
      </div>

      <div className="page-body" style={{ maxWidth: 880 }}>
        <CardSection title="Contact &amp; company" desc="Who you'll be working with and how to reach them.">
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Contact name">
              <input
                className="input"
                placeholder="Full name"
                {...register('name', { required: 'Name is required' })}
                style={errors.name ? { borderColor: 'var(--danger)' } : {}}
              />
              {errors.name && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{errors.name.message}</span>}
            </Field>
            <Field label="Email" hint="used for sign-in &amp; notifications">
              <input
                className="input"
                type="email"
                placeholder="name@company.com"
                {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' } })}
                style={errors.email ? { borderColor: 'var(--danger)' } : {}}
              />
              {errors.email && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{errors.email.message}</span>}
            </Field>
            <Field label="Password">
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })}
                  style={{ paddingRight: 96, ...(errors.password ? { borderColor: 'var(--danger)' } : {}) }}
                />
                <div style={{ position: 'absolute', inset: '0 0 0 auto', display: 'flex', alignItems: 'center', paddingRight: 8, gap: 4 }}>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--accent)', padding: '2px 6px', height: 'auto' }}
                    onClick={() => setValue('password', generatePassword(), { shouldValidate: true })}>
                    Generate
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 4px', height: 'auto' }} onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
                  </button>
                </div>
              </div>
              {errors.password && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{errors.password.message}</span>}
            </Field>
            <Field label="Company name">
              <input
                className="input"
                placeholder="Company"
                {...register('company_name', { required: 'Company name is required' })}
                style={errors.company_name ? { borderColor: 'var(--danger)' } : {}}
              />
              {errors.company_name && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{errors.company_name.message}</span>}
            </Field>
            <Field label="Industry">
              <NSSelect
                value={watch('industry') || 'general'}
                onChange={(e) => setValue('industry', e.target.value, { shouldDirty: true })}
                options={INDUSTRY_OPTIONS.map(opt => ({ value: opt.value, label: `${opt.icon} ${opt.label}` }))}
              />
            </Field>
            <Field label="Phone" hint="optional">
              <input
                className="input"
                placeholder="+44 7700 900000"
                {...register('phone')}
              />
            </Field>
            <Field label="Website" hint="optional">
              <div style={{ display: 'flex' }}>
                <span style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRight: 0, borderRadius: '10px 0 0 10px', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  https://
                </span>
                <input
                  className="input"
                  placeholder="company.com"
                  style={{ borderRadius: '0 10px 10px 0' }}
                  {...register('domain')}
                />
              </div>
            </Field>
          </div>
        </CardSection>

        <CardSection title="Plan &amp; billing" desc="Set the package and monthly edit allowance.">
          {plansLoading ? (
            <div style={{ padding: '16px 0' }}><LoadingSpinner size="sm" text="Loading plans…" /></div>
          ) : (
            <>
              <Field label="Plan" full>
                <div className="segmented" style={{ width: '100%' }}>
                  {planOptions.map(plan => (
                    <button
                      key={plan.id}
                      type="button"
                      className={subscriptionPlan === plan.id ? 'on' : ''}
                      style={{ flex: 1, justifyContent: 'center', padding: '8px 9px' }}
                      onClick={() => setValue('subscription_plan', plan.id)}
                    >
                      {plan.name}
                    </button>
                  ))}
                </div>
                {planOptions.find(p => p.id === subscriptionPlan) && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    £{planOptions.find(p => p.id === subscriptionPlan).price}/month ·{' '}
                    {planOptions.find(p => p.id === subscriptionPlan).quota === 'unlimited' ? 'Unlimited' : planOptions.find(p => p.id === subscriptionPlan).quota} edits included
                  </div>
                )}
              </Field>
              <Field label="Edit quota" hint="requests included per month">
                <input
                  className="input"
                  type="number"
                  min="1"
                  max="1000"
                  {...register('edit_quota', { required: true, min: 1 })}
                />
              </Field>
            </>
          )}
        </CardSection>

        <CardSection title="Internal notes" desc="Only your team can see this.">
          <Field label="Notes" full>
            <textarea
              className="input"
              rows={4}
              style={{ resize: 'vertical', lineHeight: 1.6 }}
              placeholder="Add context for your team — preferences, history, special arrangements…"
            />
          </Field>
        </CardSection>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingBottom: 8 }}>
          {actions}
        </div>
      </div>
    </>
  )
}

export default NewClient
